/**
 * Shared confirmation for trashing or permanently deleting a database.
 *
 * Every entry point (canvas context menu, edgeless delete key, sidebar) goes
 * through this module so the user always sees the same relation-dependency
 * warning before a database disappears.
 */

import type { useConfirmModal } from '@affine/component';
import { I18n } from '@affine/i18n';

import type {
  DatabaseDeleteDependencies,
  DatabaseRelationDependency,
} from './database-relation-dependencies';

export type DatabaseDeleteAction = 'trash' | 'permanent-delete';

export interface DatabaseDeleteConfirmRequest {
  action: DatabaseDeleteAction;
  databaseTitle: string;
  docTitle: string;
  dependencies: DatabaseDeleteDependencies;
}

type ConfirmModalHandle = Pick<
  ReturnType<typeof useConfirmModal>,
  'openConfirmModal'
>;

function formatDependencyLine(dependency: DatabaseRelationDependency): string {
  return I18n.t('com.streamlain.streamlain.database-delete.dependency-line', {
    database:
      dependency.database.databaseTitle ||
      I18n.t('com.streamlain.streamlain.relation.restricted-database'),
    doc:
      dependency.database.docTitle ||
      I18n.t('com.streamlain.streamlain.relation.restricted-document'),
    columns: dependency.columns.map(column => column.name).join(', '),
  });
}

function DependencyList({
  dependencies,
}: {
  dependencies: DatabaseRelationDependency[];
}) {
  return (
    <ul data-testid="database-delete-dependency-list">
      {dependencies.map(dependency => (
        <li
          key={`${dependency.database.docId}:${dependency.database.blockId}`}
          data-testid="database-delete-dependency"
        >
          {formatDependencyLine(dependency)}
        </li>
      ))}
    </ul>
  );
}

export function DatabaseDeleteDescription({
  request,
}: {
  request: DatabaseDeleteConfirmRequest;
}) {
  const { action, databaseTitle, docTitle, dependencies } = request;
  const writable = dependencies.dependencies.filter(entry => entry.writable);
  const readOnly = dependencies.dependencies.filter(entry => !entry.writable);
  const baseMessage =
    action === 'trash'
      ? I18n.t('com.streamlain.streamlain.database-delete.trash-message', {
          name: databaseTitle,
        })
      : I18n.t('com.streamlain.streamlain.common.delete-permanently-message', {
          name: databaseTitle,
          docTitle,
        });

  return (
    <div data-testid="database-delete-description">
      <p>{baseMessage}</p>
      {action === 'trash' && dependencies.dependencies.length > 0 && (
        <>
          <p>
            {I18n.t(
              'com.streamlain.streamlain.database-delete.dependencies-trash',
              { count: dependencies.dependencies.length }
            )}
          </p>
          <DependencyList dependencies={dependencies.dependencies} />
        </>
      )}
      {action === 'permanent-delete' && writable.length > 0 && (
        <>
          <p>
            {I18n.t(
              'com.streamlain.streamlain.database-delete.dependencies-permanent',
              { count: writable.length }
            )}
          </p>
          <DependencyList dependencies={writable} />
        </>
      )}
      {action === 'permanent-delete' && readOnly.length > 0 && (
        <>
          <p>
            {I18n.t(
              'com.streamlain.streamlain.database-delete.dependencies-readonly',
              { count: readOnly.length }
            )}
          </p>
          <DependencyList dependencies={readOnly} />
        </>
      )}
    </div>
  );
}

/**
 * Open the shared delete confirmation. Resolves `true` when the user confirms.
 */
export function confirmDatabaseDelete(
  modal: ConfirmModalHandle,
  request: DatabaseDeleteConfirmRequest
): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    modal.openConfirmModal({
      title:
        request.action === 'trash'
          ? I18n.t('com.streamlain.streamlain.database-delete.trash-title')
          : I18n.t('com.streamlain.streamlain.common.delete-permanently-title'),
      description: <DatabaseDeleteDescription request={request} />,
      confirmText:
        request.action === 'trash'
          ? I18n.t('com.streamlain.streamlain.common.move-to-trash')
          : I18n.t('com.streamlain.streamlain.common.delete-permanently'),
      cancelText: I18n.t('Cancel'),
      confirmButtonOptions: {
        variant: 'error',
        disabled:
          request.action === 'permanent-delete' &&
          request.dependencies.dependencies.some(entry => !entry.writable),
        ...({ 'data-testid': 'database-delete-confirm' } as Record<
          string,
          string
        >),
      },
      onConfirm: () => settle(true),
      onCancel: () => settle(false),
      onOpenChange: open => {
        if (!open) settle(false);
      },
    });
  });
}
